import { useTranslation } from 'react-i18next';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createPlay, updatePlay, deletePlay } from '../redux/slices/playsSlice';
import { fetchArtists } from '../redux/slices/artistsSlice';
import { toast } from 'react-toastify';
import validator from 'validator';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import List from '@mui/material/List';
import { DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import SortableItemCast from './SortableItemCast';
import SortableItemAttribute from './SortableItemAttribute';
import MenuItem from '@mui/material/MenuItem';
import Autocomplete from '@mui/material/Autocomplete';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import FileDropzone from './FileDropzone';
import WarningDialog from './WarningDialog';
import LanguageSelector from './LanguageSelector';
import { MAX_IMAGE_SIZE_1MB } from './../constants';
import { ACCEPTED_FORMATS } from './../constants';
import Checkbox from '@mui/material/Checkbox';
import { nanoid } from 'nanoid';
import { DEFAULT_LANGUAGE } from '../utils/localeUtils.js';

const empty = (value) => !validator.isEmpty(value, { ignore_whitespace: true });

const predefinedAttributesName = [
  'Autor',
  'Traducere',
  'Regie',
  'Scenografie',
  'Muzică',
  'Lumini',
  'Producător',
  'Video',
  'Coregrafie',
  'Costume',
];

const predefinedAttributes = [
  ...predefinedAttributesName.map((attr) => ({
    id: nanoid(),
    attributeName: attr,
    attributeValue: null,
    attributeName_en: null,
    attributeValue_en: null,
    attributeName_de: null,
    attributeValue_de: null,
    attributeName_hu: null,
    attributeValue_hu: null,
  })),
];

function PlaysDrawer({ open, onClose, play }) {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const isLoading = useSelector((state) => state.plays.isLoading);

  const artists = useSelector((state) => state.artists.data);
  const artistsOptions = useMemo(
    () =>
      artists.map(({ id, first_name, last_name, profile_image_url }) => ({
        id,
        label: `${first_name} ${last_name}`,
        profile_image_url,
      })),
    [artists]
  );
  const [selectedLanguage, setSelectedLanguage] = useState('ro');
  const [type, setType] = useState('');
  const [title, setTitle] = useState('');
  const [titleEn, setTitleEn] = useState('');
  const [titleDe, setTitleDe] = useState('');
  const [titleHu, setTitleHu] = useState('');
  const [description, setDescription] = useState('');
  const [descriptionEn, setDescriptionEn] = useState('');
  const [descriptionDe, setDescriptionDe] = useState('');
  const [descriptionHu, setDescriptionHu] = useState('');
  const [status, setStatus] = useState('');
  const [selectedPortraitUrl, setSelectedPortraitUrl] = useState('');
  const [selectedCoverUrl, setSelectedCoverUrl] = useState('');
  const [selectedPortrait, setSelectedPortrait] = useState(null);
  const [selectedCover, setSelectedCover] = useState(null);
  const [selectedArtist, setSelectedArtist] = useState(null);
  const [selectedArtists, setSelectedArtists] = useState([]);
  const [role, setRole] = useState('');
  const [index, setIndex] = useState(true);
  const [hasPause, setHasPause] = useState(false);
  const [warningOpen, setWarningOpen] = useState(false);
  const [length, setLength] = useState('');
  const [minRecAge, setMinRecAge] = useState('');
  const [applyToAllEvents, setApplyToAllEvents] = useState(false);
  const [attributeName, setAttributeName] = useState('');
  const [attributeValue, setAttributeValue] = useState('');
  const [attributes, setAttributes] = useState(predefinedAttributes);

  const titleMap = {
    ro: [title, setTitle],
    en: [titleEn, setTitleEn],
    de: [titleDe, setTitleDe],
    hu: [titleHu, setTitleHu],
  };

  const [titleValue, setTitleValue] = titleMap[selectedLanguage] ?? ['', () => {}];

  const descriptionMap = {
    ro: [description, setDescription],
    en: [descriptionEn, setDescriptionEn],
    de: [descriptionDe, setDescriptionDe],
    hu: [descriptionHu, setDescriptionHu],
  };

  const [descriptionValue, setDescriptionValue] = descriptionMap[selectedLanguage] ?? ['', () => {}];

  const createDisabled = useMemo(
    () =>
      isLoading ||
      !empty(title) ||
      !empty(type) ||
      !selectedCover ||
      !selectedPortrait ||
      isNaN(length) ||
      Number(length) < 0 ||
      !/^\d*$/.test(length) ||
      isNaN(minRecAge) ||
      Number(minRecAge) < 0 ||
      !/^\d*$/.test(minRecAge),
    [isLoading, type, title, selectedCover, selectedPortrait, length, minRecAge]
  );
  const updateDisabled = useMemo(
    () =>
      isLoading ||
      !empty(title) ||
      !empty(type) ||
      isNaN(length) ||
      Number(length) < 0 ||
      !/^\d*$/.test(length) ||
      isNaN(minRecAge) ||
      Number(minRecAge) < 0 ||
      !/^\d*$/.test(minRecAge),
    [isLoading, type, title, length, minRecAge]
  );

  const onPortrait = useCallback(
    (file) => {
      setSelectedPortrait(file);
    },
    [setSelectedPortrait]
  );

  const onCover = useCallback(
    (file) => {
      setSelectedCover(file);
    },
    [setSelectedCover]
  );

  const onDrawerClose = () => {
    onClose();
    setSelectedLanguage('ro');
    setTitle('');
    setTitleEn('');
    setType('');
    setStatus('');
    setDescription('');
    setDescriptionEn('');
    setSelectedCoverUrl('');
    setSelectedPortraitUrl('');
    setSelectedCover(null);
    setSelectedPortrait(null);
    setSelectedArtist(null);
    setSelectedArtists([]);
    setIndex(true);
    setLength('');
    setHasPause(false);
    setMinRecAge('');
    setApplyToAllEvents(false);
    setAttributes([]);
  };

  useEffect(() => {
    dispatch(fetchArtists());
    if (play?.attributes?.length > 0) {
      setAttributes(
        play.attributes.map((attr) => ({
          id: nanoid(),
          attributeName: attr.attribute_name,
          attributeValue: attr.attribute_value,
          attributeName_en: attr.attribute_name_en,
          attributeValue_en: attr.attribute_value_en,
          attributeName_de: attr.attribute_name_de,
          attributeValue_de: attr.attribute_value_de,
          attributeName_hu: attr.attribute_name_hu,
          attributeValue_hu: attr.attribute_value_hu,
        }))
      );
    } else {
      if (play) {
        setAttributes([]);
      } else {
        setAttributes(predefinedAttributes);
      }
    }
    if (play) {
      const {
        name,
        name_en,
        name_de,
        name_hu,
        type,
        description,
        description_en,
        description_de,
        description_hu,
        status,
        length,
        cover_image_url,
        portrait_image_url,
        cast,
        index_for_search,
        has_breaks,
        min_rec_age,
      } = play;
      setStatus(status);
      setTitle(name);
      setTitleEn(name_en || '');
      setTitleDe(name_de || '');
      setTitleHu(name_hu || '');
      setType(type);
      setDescription(description || '');
      setDescriptionEn(description_en || '');
      setDescriptionDe(description_de || '');
      setDescriptionHu(description_hu || '');
      setSelectedCoverUrl(cover_image_url);
      setSelectedPortraitUrl(portrait_image_url);
      setSelectedArtists(
        cast.map(({ first_name, last_name, ...rest }) => ({
          label: `${first_name} ${last_name}`,
          ...rest,
        }))
      );
      setIndex(Boolean(index_for_search));
      setLength(String(length || ''));
      setMinRecAge(String(min_rec_age || ''));
      setHasPause(Boolean(has_breaks));
    }
  }, [open, play, dispatch]);

  const onCreatePlay = () => {
    const payload = {
      name: title.trim(),
      name_en: titleEn.trim(),
      name_de: titleDe.trim(),
      name_hu: titleHu.trim(),
      type: type.trim(),
      description: description.trim() || undefined,
      description_en: descriptionEn.trim() || undefined,
      description_de: descriptionDe.trim() || undefined,
      description_hu: descriptionHu.trim() || undefined,
      portrait: selectedPortrait,
      cover: selectedCover,
      cast: selectedArtists.map(({ id, role, role_en, role_de, role_hu }) => ({ id, role, role_en, role_de, role_hu })),
      index_for_search: index,
      length: length.trim(),
      has_breaks: hasPause,
      min_rec_age: minRecAge.trim(),
      attributes: attributes,
    };
    dispatch(createPlay(payload)).then(({ payload }) => {
      if (payload) {
        toast.success(t('Play created successfuly'), { position: toast.POSITION.BOTTOM_RIGHT });
        onDrawerClose();
      }
    });
  };

  const onUpdatePlay = () => {
    const payload = {
      playId: play.id,
      name: title.trim(),
      name_en: titleEn.trim(),
      name_de: titleDe.trim(),
      name_hu: titleHu.trim(),
      type: type.trim(),
      status,
      description: description.trim() || undefined,
      description_en: descriptionEn.trim() || undefined,
      description_de: descriptionDe.trim() || undefined,
      description_hu: descriptionHu.trim() || undefined,
      portrait: selectedPortrait,
      cover: selectedCover,
      cast: selectedArtists.map(({ id, role, role_en, role_de, role_hu }) => ({ id, role, role_en, role_de, role_hu })),
      index_for_search: index,
      length: length.trim(),
      has_breaks: hasPause,
      min_rec_age: minRecAge.trim(),
      apply_to_all_events: applyToAllEvents,
      attributes: attributes,
    };
    dispatch(updatePlay(payload)).then(({ payload }) => {
      if (payload) {
        toast.success(t('Play updated successfuly'), { position: toast.POSITION.BOTTOM_RIGHT });
        onDrawerClose();
      }
    });
  };

  const addArtist = () => {
    setSelectedArtists((current) => [...current, { ...selectedArtist, role }]);
    setRole('');
    setSelectedArtist(null);
  };

  const removeArtist = (idToRemove) => {
    const idx = selectedArtists.findIndex(({ id }) => idToRemove === id);
    const newState = [...selectedArtists];
    newState.splice(idx, 1);
    setSelectedArtists(newState);
  };

  const updateArtistRole = (artistId, newRole, lang) => {
    setSelectedArtists((prev) =>
      prev.map((artist) =>
        artist.id === artistId
          ? {
              ...artist,
              ...(lang === 'ro' ? { role: newRole } : { [`role_${lang}`]: newRole }),
            }
          : artist
      )
    );
  };

  const updateAttributeName = (itemId, newValue, lang) => {
    setAttributes((prev) =>
      prev.map((attr) =>
        attr.id === itemId
          ? {
              ...attr,
              ...(lang === 'ro' ? { attributeName: newValue } : { [`attributeName_${lang}`]: newValue }),
            }
          : attr
      )
    );
  };

  const updateAttributeValue = (itemId, newValue, lang) => {
    setAttributes((prev) =>
      prev.map((attr) =>
        attr.id === itemId
          ? {
              ...attr,
              ...(lang === 'ro' ? { attributeValue: newValue } : { [`attributeValue_${lang}`]: newValue }),
            }
          : attr
      )
    );
  };

  const addAttribute = () => {
    setAttributes((current) => [...current, { id: nanoid(), attributeName, attributeValue }]);
    setAttributeName('');
    setAttributeValue('');
  };

  const removeAttribute = (idToRemove) => {
    const idx = attributes.findIndex(({ id }) => idToRemove === id);
    const newState = [...attributes];
    newState.splice(idx, 1);
    setAttributes(newState);
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 150,
        tolerance: 5,
      },
    })
  );

  const handleDragEndArtist = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = selectedArtists.findIndex((a) => a.id === active.id);
    const newIndex = selectedArtists.findIndex((a) => a.id === over.id);
    setSelectedArtists((prev) => arrayMove(prev, oldIndex, newIndex));
  };

  const handleDragEndAttributes = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setAttributes((prev) => {
      const oldIndex = prev.findIndex((item) => item.id === active.id);
      const newIndex = prev.findIndex((item) => item.id === over.id);

      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  const getOptionDisabled = (option) => {
    const { id } = option;
    return !!selectedArtists.find((artist) => artist.id === id);
  };

  const toggleOpenWarning = () => {
    setWarningOpen(!warningOpen);
  };

  const onDeletePlay = () => {
    const payload = {
      playId: play.id,
    };
    dispatch(deletePlay(payload)).then(({ payload }) => {
      if (payload) {
        toast.success(t('Play deleted successfuly'), { position: toast.POSITION.BOTTOM_RIGHT });
        toggleOpenWarning();
        onDrawerClose();
      }
    });
  };

  return (
    <Drawer anchor="right" open={open}>
      <Toolbar />
      <Toolbar>
        <Typography variant="button">{!play ? t('Create play') : t('Edit play')}</Typography>
      </Toolbar>
      <Divider />
      <Box sx={{ width: 400, pl: 2, pr: 2 }}>
        <Box component="form">
          <TextField
            required
            value={type}
            onChange={(event) => setType(event.target.value)}
            variant="standard"
            margin="normal"
            size="small"
            fullWidth
            select
            label={t('Type')}
          >
            <MenuItem value={'THEATER'}>{t('THEATER')}</MenuItem>
            <MenuItem value={'OPERA'}>{t('OPERA')}</MenuItem>
            <MenuItem value={'OPERETTA'}>{t('OPERETTA')}</MenuItem>
            <MenuItem value={'MUSICAL'}>{t('MUSICAL')}</MenuItem>
            <MenuItem value={'BALLET'}>{t('BALLET')}</MenuItem>
            <MenuItem value={'CONCERT'}>{t('CONCERT')}</MenuItem>
            <MenuItem value={'SYMPHONIC_CONCERT'}>{t('SYMPHONIC_CONCERT')}</MenuItem>
            <MenuItem value={'VOCAL_SYMPHONIC_CONCERT'}>{t('VOCAL_SYMPHONIC_CONCERT')}</MenuItem>
            <MenuItem value={'A_CAPPELLA_CONCERT'}>{t('A_CAPPELLA_CONCERT')}</MenuItem>
            <MenuItem value={'CHAMBER_CONCERT'}>{t('CHAMBER_CONCERT')}</MenuItem>
            <MenuItem value={'OPERA_IN_CONCERT'}>{t('OPERA_IN_CONCERT')}</MenuItem>
            <MenuItem value={'RECITAL'}>{t('RECITAL')}</MenuItem>
            <MenuItem value={'GALA'}>{t('GALA')}</MenuItem>
          </TextField>
          <LanguageSelector language={selectedLanguage} setLanguage={setSelectedLanguage} isDrawer={true} />
          <TextField
            required={selectedLanguage === 'ro'}
            value={titleValue}
            onChange={(event) => setTitleValue(event.target.value)}
            fullWidth
            variant="standard"
            margin="normal"
            size="small"
            name="title"
            label={t('Title')}
          />
          {play && (
            <TextField
              value={status}
              onChange={(event) => setStatus(event.target.value)}
              variant="standard"
              margin="normal"
              size="small"
              fullWidth
              select
              label={t('Status')}
            >
              <MenuItem value={'ACTIVE'}>{t('ACTIVE')}</MenuItem>
              <MenuItem value={'ARCHIVED'}>{t('ARCHIVED')}</MenuItem>
            </TextField>
          )}
          {play && (
            <FormControlLabel
              control={
                <Checkbox checked={applyToAllEvents} onChange={(event) => setApplyToAllEvents(event.target.checked)} />
              }
              label={t('Apply the changes for all created events')}
              labelPlacement="end"
            />
          )}
          <TextField
            multiline
            minRows={5}
            maxRows={10}
            value={descriptionValue}
            onChange={(event) => setDescriptionValue(event.target.value.replace('\n', '\r\n'))}
            fullWidth
            variant="standard"
            margin="normal"
            size="small"
            name="description"
            label={t('Details')}
          />
          <Box mt={2}>
            <Typography sx={{ display: 'inline-block', marginBottom: 2 }} variant="button">
              {t('PROPERTIES')}
            </Typography>
            <TextField
              value={attributeName}
              onChange={(event) => setAttributeName(event.target.value)}
              fullWidth
              variant="standard"
              margin="normal"
              size="small"
              disabled={selectedLanguage !== DEFAULT_LANGUAGE}
              name="attributeName"
              label={t('Attribute Name')}
              inputProps={{ maxLength: 45 }}
            />
            <Button
              sx={{ marginTop: 2 }}
              onClick={addAttribute}
              disabled={!attributeName || selectedLanguage !== DEFAULT_LANGUAGE}
              fullWidth
              variant="text"
              size="small"
            >
              {t('Add')}
            </Button>
            <List>
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEndAttributes}>
                <SortableContext items={attributes.map((a) => a.id)} strategy={verticalListSortingStrategy}>
                  {attributes.map((item) => (
                    <SortableItemAttribute
                      key={item.id}
                      item={item}
                      removeAttribute={removeAttribute}
                      updateAttributeName={updateAttributeName}
                      updateAttributeValue={updateAttributeValue}
                      selectedLanguage={selectedLanguage}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            </List>
          </Box>
          <TextField
            value={length}
            onChange={(event) => setLength(event.target.value)}
            fullWidth
            variant="standard"
            margin="normal"
            size="small"
            name="length"
            label={t('Length')}
            helperText={t('Expressed in minutes')}
          />
          <FormGroup sx={{ display: 'block', mt: 2, mb: 2 }}>
            <FormControlLabel
              control={<Switch checked={hasPause} onChange={(event) => setHasPause(event.target.checked)} />}
              label={t('With breaks')}
            />
          </FormGroup>
          <TextField
            value={minRecAge}
            onChange={(event) => setMinRecAge(event.target.value)}
            fullWidth
            variant="standard"
            margin="normal"
            size="small"
            name="min_rec_age"
            label={t('Min reccomended age')}
            helperText={t('Expressed in years')}
          />
          <Box mt={2}>
            <Typography sx={{ display: 'inline-block', marginBottom: 2 }} variant="button">
              {t('Cast *')}
            </Typography>
            <Autocomplete
              autoComplete
              getOptionDisabled={getOptionDisabled}
              value={selectedArtist}
              onChange={(event, newValue) => {
                setSelectedArtist(newValue);
              }}
              disablePortal
              options={artistsOptions}
              disabled={selectedLanguage !== DEFAULT_LANGUAGE}
              fullWidth
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="standard"
                  name="artists"
                  label={t('Search artist')}
                  placeholder="Name..."
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  {option.label}
                </li>
              )}
            />
            <TextField
              value={role}
              onChange={(event) => setRole(event.target.value)}
              fullWidth
              disabled={selectedLanguage !== DEFAULT_LANGUAGE}
              variant="standard"
              margin="normal"
              size="small"
              name="role"
              label={t('Artist role')}
              inputProps={{ maxLength: 45 }}
            />
            <Button
              sx={{ marginTop: 2 }}
              onClick={addArtist}
              disabled={!selectedArtist || selectedLanguage !== DEFAULT_LANGUAGE}
              fullWidth
              variant="text"
              size="small"
            >
              {t('Add')}
            </Button>
            <List>
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEndArtist}>
                <SortableContext items={selectedArtists.map((a) => a.id)} strategy={verticalListSortingStrategy}>
                  {selectedArtists.map((artist) => (
                    <SortableItemCast
                      key={artist.id}
                      artist={artist}
                      removeArtist={removeArtist}
                      updateArtistRole={updateArtistRole}
                      selectedLanguage={selectedLanguage}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            </List>
          </Box>
          <Box mt={2}>
            <Typography sx={{ display: 'inline-block', marginBottom: 2 }} variant="button">
              {t('Portrait Image *')}
            </Typography>
            <FileDropzone
              sx={{ height: 300 }}
              previewUrl={selectedPortraitUrl}
              preview
              fileTypes={ACCEPTED_FORMATS}
              maxSize={MAX_IMAGE_SIZE_1MB}
              onDrop={onPortrait}
            />
          </Box>
          <Box mt={2} mb={2}>
            <Typography sx={{ display: 'inline-block', marginBottom: 2 }} variant="button">
              {t('Cover Image *')}
            </Typography>
            <FileDropzone
              sx={{ height: 300 }}
              previewUrl={selectedCoverUrl}
              preview
              fileTypes={ACCEPTED_FORMATS}
              maxSize={MAX_IMAGE_SIZE_1MB}
              onDrop={onCover}
            />
          </Box>
          <Typography variant="body2">{t('Max size: 1MB')}</Typography>
          <Typography sx={{ mb: 2 }} variant="body2">
            {t('File format: JPEG,JPG,PNG')}
          </Typography>
          <FormGroup sx={{ display: 'block', mt: 2, mb: 2 }}>
            <FormControlLabel
              control={<Switch checked={index} onChange={(event) => setIndex(event.target.checked)} />}
              label={t('Index in main site')}
            />
          </FormGroup>
        </Box>
        {play && (
          <>
            <Button disabled={!play.can_delete} onClick={toggleOpenWarning} fullWidth variant="outlined">
              {t('Delete')}
            </Button>
            <WarningDialog
              open={warningOpen}
              loading={isLoading}
              onCancel={toggleOpenWarning}
              onConfirm={onDeletePlay}
              title={t('Warning')}
              content={t('Are you sure you want to delete this play?')}
            />
          </>
        )}
      </Box>
      <Box sx={{ p: 2, mt: 'auto' }}>
        <Grid columnSpacing={2} container>
          <Grid xs={6} item>
            <Button fullWidth variant="outlined" onClick={onDrawerClose}>
              {t('Cancel')}
            </Button>
          </Grid>
          <Grid xs={6} item>
            <Button
              disabled={!play ? createDisabled : updateDisabled}
              fullWidth
              variant="contained"
              onClick={!play ? onCreatePlay : onUpdatePlay}
            >
              {!play ? t('Create') : t('Update')}
              {isLoading && <CircularProgress size={24} sx={{ ml: 1, color: 'white' }} />}
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Drawer>
  );
}

export default PlaysDrawer;
